import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { ArrowRight, TreeDeciduous } from 'lucide-react'
import { buttonClass } from '@/components/generals/button'

import { HERO } from '@/constants/content'

const HeroSection: FC = (): ReactNode => {
    return (
        <section id='hero' className='relative'>
            <div className='mx-auto grid max-w-6xl items-center gap-14 px-5 pt-10 pb-20 md:px-8 md:pt-16 md:pb-28 lg:grid-cols-[1.1fr_0.9fr]'>
                <div className='animate-fade-rise'>
                    <p className='flex items-center gap-3 text-xs font-semibold tracking-[0.2em] text-clay uppercase'>
                        <span aria-hidden='true' className='h-px w-8 bg-gold' />
                        {HERO.eyebrow}
                    </p>

                    <h1 className='mt-6 font-heading text-[2.75rem] leading-[1.05] font-semibold tracking-tight text-balance text-ink sm:text-6xl lg:text-[4.25rem]'>
                        {HERO.title}
                    </h1>

                    <p className='mt-6 max-w-xl text-lg leading-relaxed text-pretty text-umber md:text-xl'>
                        {HERO.text}
                    </p>

                    <div className='mt-10 flex flex-wrap gap-3'>
                        <Link href='/tree' className={buttonClass({ size: 'lg', className: 'group' })}>
                            {HERO.primary}
                            <ArrowRight className='size-5 transition-transform duration-200 group-hover:translate-x-1' aria-hidden='true' />
                        </Link>

                        <Link href='/share' className={buttonClass({ variant: 'secondary', size: 'lg' })}>
                            {HERO.secondary}
                        </Link>
                    </div>
                </div>

                <div className='relative mx-auto w-full max-w-md animate-fade-rise [animation-delay:150ms] lg:max-w-none'>
                    <div aria-hidden='true' className='absolute -inset-3 rotate-2 rounded-[2.5rem] border border-gold/40' />

                    <figure className='relative overflow-hidden rounded-t-[12rem] rounded-b-[2rem] border-8 border-linen bg-linen shadow-lifted'>
                        <img
                            src='/images/hero-family-photos.jpg'
                            alt={HERO.imageAlt}
                            width={1500}
                            height={1500}
                            className='aspect-[4/5] w-full object-cover object-center sepia-[0.25]'
                        />
                    </figure>

                    <div className='absolute -bottom-6 -left-4 flex items-center gap-3 rounded-2xl border border-line bg-linen px-5 py-4 shadow-lifted sm:-left-8'>
                        <span className='grid size-11 place-items-center rounded-full bg-forest-soft text-forest'>
                            <TreeDeciduous className='size-5' aria-hidden='true' />
                        </span>

                        <span>
                            <span className='block font-heading text-lg leading-tight font-semibold text-ink'>
                                Every branch,
                            </span>
                            <span className='block text-sm text-umber'>
                                every story, one family
                            </span>
                        </span>
                    </div>
                </div>
            </div>
        </section>
    )
}

export default HeroSection
